// External Libraries
import { useImmerReducer } from "use-immer";

// Internal Utilities
import { deepCopy } from "../../../../../helper";

// Action constants
const SET_SENT_ON_FILTER = "SET_SENT_ON_FILTER";
const SET_DUE_DATE_FILTER = "SET_DUE_DATE_FILTER";
const TOGGLE_STATUS_FILTER = "TOGGLE_STATUS_FILTER";
const TOGGLE_ALL_STATUS_FILTER = "TOGGLE_ALL_STATUS_FILTER";
const SET_ENTIRE_STATE = "SET_ENTIRE_STATE";
const CLEAR_ALL_FILTERS = "CLEAR_ALL_FILTERS";
const TOGGLE_REQUESTS_FILTER = "TOGGLE_REQUESTS_FILTER";
const TOGGLE_SELECT_ALL_REQUESTS = "TOGGLE_SELECT_ALL_REQUESTS";

export const initialFilterState = {
  sentOnFilter: { option: "On", date: "", secondDate: "" },
  dueDateFilter: { option: "On", date: "", secondDate: "" },
  statusFilters: {
    done: false,
    pending: false,
    cancelled: false,
    expired: false,
  },
  requestsFilter: {
    onlyMyRequests: false,
    othersRequests: false,
    recurringRequest: false,
  },
};

// Reducer function
const filterReducer = (state, action) => {
  switch (action.type) {
    case SET_SENT_ON_FILTER:
      state.sentOnFilter[action.payload.property] = action.payload.value;
      break;
    case SET_DUE_DATE_FILTER:
      state.dueDateFilter[action.payload.property] = action.payload.value;
      break;
    case TOGGLE_STATUS_FILTER:
      state.statusFilters[action.payload.property] = action.payload.value;
      break;
    case TOGGLE_ALL_STATUS_FILTER:
      const allStatusChecked = Object.values(state.statusFilters).every(
        Boolean
      );
      for (const status in state.statusFilters) {
        state.statusFilters[status] = !allStatusChecked;
      }
      break;
    case TOGGLE_REQUESTS_FILTER:
      state.requestsFilter[action.payload.property] = action.payload.value;
      break;
    case TOGGLE_SELECT_ALL_REQUESTS:
      const allRequestChecked = Object.values(state.requestsFilter).every(
        Boolean
      );
      for (const status in state.requestsFilter) {
        state.requestsFilter[status] = !allRequestChecked;
      }
      break;
    case SET_ENTIRE_STATE:
      return action.payload;
    case CLEAR_ALL_FILTERS:
      state.sentOnFilter = { option: "On", date: "", secondDate: "" };
      state.dueDateFilter = { option: "On", date: "", secondDate: "" };
      state.statusFilters = {
        done: false,
        pending: false,
        cancelled: false,
        expired: false,
      };
      state.requestsFilter = {
        onlyMyRequests: false,
        othersRequests: false,
        recurringRequest: false,
      };
      break;
    default:
      // Handle unknown type
      break;
  }
};

// Custom hook for filters
const useFilter = () => {
  // Initial state using useImmerReducer
  const [state, dispatch] = useImmerReducer(
    filterReducer,
    deepCopy(initialFilterState)
  );

  // Function for handling changes
  const handleChange = (type, ...args) => {
    const [tab = "noTab", option = "noOption", date = ""] = args;
    switch (type) {
      case "date":
        dispatch({
          type: tab === "Sent On" ? SET_SENT_ON_FILTER : SET_DUE_DATE_FILTER,
          payload: { property: option, value: date },
        });
        break;
      case "secondDate":
        dispatch({
          type: tab === "Sent On" ? SET_SENT_ON_FILTER : SET_DUE_DATE_FILTER,
          payload: { property: "secondDate", value: date },
        });
        break;
      case "status":
        const [status] = args;
        dispatch({
          type: TOGGLE_STATUS_FILTER,
          payload: { property: status, value: !state.statusFilters[status] },
        });
        break;
      case "selectAllStatus":
        dispatch({
          type: TOGGLE_ALL_STATUS_FILTER,
        });
        break;
      case "requests":
        const [requestType] = args;
        dispatch({
          type: TOGGLE_REQUESTS_FILTER,
          payload: {
            property: requestType,
            value: !state.requestsFilter[requestType],
          },
        });
        break;
      case "selectAllRequest":
        dispatch({
          type: TOGGLE_SELECT_ALL_REQUESTS,
        });
        break;
      case "clearAllFilters":
        dispatch({ type: CLEAR_ALL_FILTERS });
        break;
      case "setEntireState":
        const [newState] = args;
        dispatch({
          type: SET_ENTIRE_STATE,
          payload: newState,
        });
        break;
      default:
        // Handle unknown type
        break;
    }
  };

  // Return the state, current active tab, and the common change handler
  return {
    state,
    handleChange,
  };
};

export default useFilter;
